import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import NeoButton from "../../../components/Common/NeoButton";
import NeoCard from "../../../components/Common/NeoCard";
import NeoBadge from "../../../components/Common/NeoBadge";
import NeoSearch from "../../../components/Common/NeoSearch";
import Breadcrumb from "../../../components/Common/Breadcrumb";
import TicketDetailModal from "./components/TicketDetailModal";
import TicketFormModal from "./components/TicketFormModal";
import usePlatformSupport from "./hooks/usePlatformSupport";
import { PRIORITY_COLORS, KANBAN_COLUMNS, TICKET_FORM_DEFAULTS } from "./constants";

const PlatformSupport = () => {
    const { items, saveTicket } = usePlatformSupport();
    const navigate = useNavigate();

    const [selectedTicket, setSelectedTicket] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [newComment, setNewComment] = useState("");
    const [search, setSearch] = useState("");
    const [formTicket, setFormTicket] = useState(TICKET_FORM_DEFAULTS);

    const openTicketDetails = (ticket) => {
        setSelectedTicket(ticket);
        setNewComment("");
        setModalOpen(true);
    };

    const handleAddComment = async () => {
        if (!selectedTicket || !newComment.trim()) return;
        const updated = {
            ...selectedTicket,
            comments: [
                ...(selectedTicket.comments || []),
                { user: "Admin", text: newComment, time: new Date().toLocaleTimeString("pt-BR") }
            ],
        };
        await saveTicket(updated);
        setSelectedTicket(updated);
        setNewComment("");
    };

    const handleUpdateStatus = async (ticketId, nextStatus) => {
        const ticket = items.find(t => t.id === ticketId);
        if (!ticket) return;
        await saveTicket({ ...ticket, status: nextStatus });
        if (selectedTicket?.id === ticketId) setSelectedTicket({ ...selectedTicket, status: nextStatus });
    };

    const handleCreateTicket = async () => {
        await saveTicket({
            ...formTicket,
            status: "open",
            date: new Date().toISOString().split("T")[0],
            comments: [],
        });
        setCreateModalOpen(false);
        setFormTicket(TICKET_FORM_DEFAULTS);
    };

    const filteredTickets = items.filter(t => {
        const q = search.toLowerCase();
        return (
            (t.title || "").toLowerCase().includes(q) ||
            (t.tenantName || t.schoolName || "").toLowerCase().includes(q) ||
            (t.description || "").toLowerCase().includes(q)
        );
    });

    const channelIcon = (ch) => {
        switch (ch) {
            case "whatsapp": return "mdi-whatsapp";
            case "email": return "mdi-email-outline";
            case "phone": return "mdi-phone-outline";
            case "chat": return "mdi-chat-outline";
            default: return "mdi-headset";
        }
    };

    const renderColumn = (statusKey, label) => {
        const columnTickets = filteredTickets.filter(t => t.status === statusKey);
        return (
            <div key={statusKey} className="platform-kanban-column">
                <div className="platform-kanban-column-header">
                    <h6 className="platform-kanban-column-title">{label}</h6>
                    <NeoBadge color="muted">{columnTickets.length}</NeoBadge>
                </div>
                <div>
                    {columnTickets.map((t) => (
                        <div key={t.id} className="platform-kanban-card" onClick={() => openTicketDetails(t)}>
                            <div className="d-flex justify-content-between align-items-start mb-2">
                                <span className="text-xs text-muted fw-bold">
                                    #{(t.id || "").substring(0, 6).toUpperCase()}
                                    {t.category && <span className="ms-1">• {t.category}</span>}
                                </span>
                                <NeoBadge color={PRIORITY_COLORS[t.priority] || "muted"}>{t.priority}</NeoBadge>
                            </div>
                            <h6 className="fw-bold text-dark text-sm m-0 mb-1">{t.title}</h6>
                            {t.idTenant ? (
                                <span
                                    className="text-xs text-primary fw-semibold d-block mb-2"
                                    style={{ cursor: "pointer" }}
                                    title="Ver detalhes do cliente"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/platform/tenants/${t.idTenant}`);
                                    }}
                                >
                                    <i className="ph-light ph-buildings align-middle me-1" />
                                    {t.tenantName || t.schoolName || "—"}
                                </span>
                            ) : (
                                <span className="text-xs text-secondary d-block mb-2">{t.tenantName || t.schoolName || "—"}</span>
                            )}
                            <div className="d-flex justify-content-between align-items-center border-top pt-2 mt-2">
                                <div className="d-flex align-items-center gap-2">
                                    <span className="text-xs text-muted">
                                        <i className="ph-light ph-clock align-middle me-1" />
                                        {t.createdAt
                                            ? new Date(t.createdAt).toLocaleDateString("pt-BR")
                                            : t.date
                                            ? new Date(t.date).toLocaleDateString("pt-BR")
                                            : "—"}
                                    </span>
                                    {t.channel && (
                                        <span className="text-xs text-muted">
                                            <i className={`mdi ${channelIcon(t.channel)} align-middle`} />
                                        </span>
                                    )}
                                </div>
                                <span className="text-xs text-primary fw-semibold">
                                    <i className="ph-light ph-chat-text align-middle me-1" />
                                    {(t.comments || []).length}
                                </span>
                            </div>
                        </div>
                    ))}
                    {columnTickets.length === 0 && (
                        <div className="text-center py-4 text-muted text-xs">Sem chamados nesta coluna.</div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="pb-5">
            <div className="mb-4">
                <Breadcrumb title="Suporte" />
            </div>

            <NeoCard
                title="Quadro de Suporte & Chamados"
                subtitle="Gerencie as dúvidas e chamados abertos pelas academias de forma ágil."
                headerIcon="mdi-headset"
                headerActions={
                    <NeoButton variant="primary" size="sm" onClick={() => setCreateModalOpen(true)}>
                        <i className="ph-light ph-plus fs-5 me-1" /> Abrir Chamado
                    </NeoButton>
                }
                thinBorder={true}
                bodyClassName="p-4"
            >
                <div className="mb-4" style={{ maxWidth: "350px" }}>
                    <NeoSearch
                        size="sm"
                        placeholder="Buscar chamados..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onClear={() => setSearch("")}
                    />
                </div>

                <div className="platform-kanban">
                    {KANBAN_COLUMNS.map(col => renderColumn(col.key, col.label))}
                </div>
            </NeoCard>

            <TicketDetailModal
                isOpen={modalOpen}
                toggle={() => setModalOpen(false)}
                ticket={selectedTicket}
                newComment={newComment}
                onChangeComment={setNewComment}
                onAddComment={handleAddComment}
                onUpdateStatus={handleUpdateStatus}
            />

            <TicketFormModal
                isOpen={createModalOpen}
                toggle={() => setCreateModalOpen(false)}
                formTicket={formTicket}
                onChangeField={(field, val) => setFormTicket(prev => ({ ...prev, [field]: val }))}
                onSave={handleCreateTicket}
            />
        </div>
    );
};

export default PlatformSupport;
